import React, { useEffect, useCallback, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';
import {
  FormCard,
  YupNumeric,
  YupDate,
  YupString,
  YupBoolean,
  promiseModalDialog,
  isNumeric,
  toastSuccess,
} from 'motion-components';
import * as Yup from 'yup';
import * as Lo from 'lodash';
import { gqlGetByIdQuery, gqlAdd, gqlUpdate, gqlDelete, FormInput, gqlAccept } from '../graphql';
import '../../../../styles/tab-styles.css';
import { FormFields } from './form-fields';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import { ButtonCloseModal, RowFieldTooltips } from 'shared-pages/components';
import { requiredString } from 'shared-pages/common/constants';
import { getRouteByName } from 'app/routes';

type Props = {
  isModal?: boolean;
  paramId?: string | undefined;
  paramCollectorId?: number | undefined;
  modalOnClose?: (id: number) => void | undefined;
};

export const VehicleFormPage = ({
  isModal = false,
  paramId = undefined,
  paramCollectorId,
  modalOnClose,
}: Props) => {
  const { handleGraphqlError } = useHandleError('vehicle');
  const { appStore } = useRootStore();
  const history = useHistory();
  const location: any = useLocation();
  const { id: urlId } = useParams<{ id: string }>();
  const id = isModal ? paramId : urlId;
  const idNum = isNumeric(id) ? parseInt(id) : -1;
  const isNew = idNum < 0;
  const { t } = useTranslation();

  const formShape = {
    id: YupNumeric,
    plateNumber: YupString.required(t(requiredString)),
    notes: YupString,
    grossWeight: Yup.number()
      .typeError('Στο πεδίο πρέπει να συμπληρωθεί αριθμός')
      .min(1, 'Ελάχιστη τιμή 1')
      .max(200000, 'Μέγιστη Τιμή 200000')
      .nullable(),
    payloadWeight: Yup.number()
      .typeError('Στο πεδίο πρέπει να συμπληρωθεί αριθμός')
      .min(1, 'Ελάχιστη τιμή 1')
      .max(200000, 'Μέγιστη Τιμή 200000')
      .nullable(),
    collectorId: YupNumeric.required(t(requiredString)),
    isActive: YupBoolean,
    isApproved: YupBoolean,
    approvedDate: YupDate,
    approvedBy: YupString,
    licenseFileId: YupNumeric,
  };

  const validationSchema = Yup.object().shape(formShape);
  const pickProperties = (values) => Lo.pick(values, Lo.keys(formShape));
  type FormValuesType = Yup.InferType<typeof validationSchema>;

  const [addNewRecord] = useMutation(gqlAdd);
  const [updateRecord] = useMutation(gqlUpdate);
  const [deleteRecord] = useMutation(gqlDelete);
  const [accept] = useMutation(gqlAccept);
  const [get, { data: rawData, error, refetch }] = useLazyQuery(gqlGetByIdQuery);
  const data = rawData ? rawData[Object.keys(rawData)[0]] : undefined;
  const [initialNewRecData, setInitialNewRecData] = useState<any>();

  /** Fetch data or Initialize new record */
  useEffect(() => {
    if (isNew) {
      // initialize new record
      initializeNewRecord();
    } else {
      // fetch data
      get({ variables: { id: idNum } });
    }
  }, [get, id, idNum]);

  /** Αρχικοποίησε Νέα Εγγραφή  */
  const initializeNewRecord = async () => {
    let newRec: any = { isActive: true };
    if (appStore.isCollector) {
      newRec = {
        ...newRec,
        collectorId: appStore.associatedId,
      };
    } else if (paramCollectorId) {
      newRec = {
        ...newRec,
        collectorId: paramCollectorId,
      };
    }
    setInitialNewRecData(newRec);
  };

  // Handler να πάει στην πίσω σελίδα
  const handleReturn = useCallback(() => {
    history.push(generatePath(getRouteByName('Vehicles')), { ...location.state });
  }, [history, location.state]);

  /**
   * * ΑΠΟΘΗΚΕΥΣΗ - UPDATE/CREATE
   */
  const handleSubmit = async (values: FormValuesType, actions) => {
    actions.setSubmitting(false);
    const mutValues = Lo.pick(values, Lo.keys(new FormInput()));

    // Αν είναι νέα εγγραφή τοτε γράφω όλα τα πεδία
    if (isNew) {
      console.log('[IDoc] Add:', mutValues);
      const rec = await addNewRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          const rec = d.data[Object.keys(d.data)[0]];
          toastSuccess(`Επιτυχής Προσθήκη Εγγραφής [${rec.id}]`);
          return rec;
        })
        .catch((error) => {
          handleGraphqlError('mutation-add', error);
          return null;
        });
      if (rec) {
        if (isModal) {
          if (modalOnClose) modalOnClose(rec.id);
        }
      }
    } else {
      // Αν είναι update εγγραφής τοτε γράφω μονο τα αλλαγμένα πεδία
      console.log('[Applog] Update:', mutValues);
      updateRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          toastSuccess(`Επιτυχής Ενημέρωση Εγγραφής [${d.data[Object.keys(d.data)[0]].id}]`);
          refetch();
        })
        .catch((error) => {
          handleGraphqlError('mutation-update', error);
        });
    }
  };

  /**
   * *  DELETE
   */
  const handleDelete = useCallback(async () => {
    const res = await promiseModalDialog({
      title: 'Διαγραφή Εγγραφής',
      text: 'Είστε σίγουροι ότι θέλετε να διαγράψετε την Εγγραφή;',
    });
    if (res) {
      deleteRecord({ variables: { id: idNum } })
        .then(() => history.goBack())
        .catch((error) => {
          handleGraphqlError('mutation-delete', error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRecord, idNum]);

  /**
   * * APPROVE
   */
  const handleApprove = async () => {
    accept({ variables: { id: idNum } })
      .then((d) => {
        toastSuccess(`${t('Το όχημα εγκρίθηκε!')} [${d.data.acceptVehicle}]`);
        refetch();
      })
      .catch((error) => {
        handleGraphqlError('mutation-accept', error);
      });
  };

  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  } else {
    return (
      <FormCard
        title={isNew ? `${t('Δήλωση Νέου Οχήματος')}` : `${t('Όχημα')}: ${data?.plateNumber}`}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        onReturn={!isModal ? handleReturn : null}
        permitSubmit={false}
        permitDelete={false}
        initialValues={isNew ? initialNewRecData : pickProperties(data)}
        validationSchema={validationSchema}
        enableReinitialize
        idx={idNum}
        injectFooterElements={
          !isModal
            ? undefined
            : (formikCtx) => {
                return (
                  <ButtonCloseModal
                    onClick={() => {
                      modalOnClose(-1);
                    }}
                  />
                );
              }
        }
      >
        <FormFields
          isNew={isNew}
          updateRecord={updateRecord}
          handleApprove={handleApprove}
          refetch={refetch}
          data={data}
        />
        {appStore.isPlatformOwner && <RowFieldTooltips data={data} />}
      </FormCard>
    );
  }
};
